import { useRef, useEffect } from 'react'
import { useTranslation } from 'react-i18next'

import { Flex, IllustratedMessage, Loader, MessageInput, SidePanel } from '@percent/lemonade'
import styles from './CandidateComments.module.scss'
import { useServices } from '@percent/partner-dashboard/context/serviceContext/ServiceContext'
import { useMutation, useRequiredParams } from '@percent/partner-dashboard/common/hooks'
import { CandidateComment } from './candidateComment/CandidateComment'
import { CandidateCommentsProps } from './CandidateComments.types'

const scrollToBottom = (ref: React.RefObject<HTMLDivElement>, delay = 300) => {
  setTimeout(() => {
    ref.current?.scrollTo({
      top: ref.current.scrollHeight,
      behavior: 'smooth'
    })
  }, delay)
}

export function CandidateComments({ onClose, commentsQuery }: Readonly<CandidateCommentsProps>) {
  const { id: programId, candidateId } = useRequiredParams()
  const commentsContainerRef = useRef<HTMLDivElement>(null)
  const previousScrollHeightRef = useRef(1000)
  const { grantsService } = useServices()
  const { t } = useTranslation()

  const { data: comments, isLoading: isLoadingComments, totalResults, nextPage, fetchLatestPage } = commentsQuery

  const [{ isLoading: isCreateCommentLoading }, { apiFunc: createComment }] = useMutation(
    grantsService.createCandidateComment,
    () => {
      fetchLatestPage()
      scrollToBottom(commentsContainerRef)
    }
  )

  useEffect(() => {
    const container = commentsContainerRef.current

    if (!container) return

    setTimeout(() => {
      const newScrollHeight = container?.scrollHeight
      container.scrollTop = newScrollHeight - previousScrollHeightRef.current
      previousScrollHeightRef.current = newScrollHeight
    }, 0)
  }, [comments])

  useEffect(() => {
    scrollToBottom(commentsContainerRef)
  }, [])

  const handleScroll = () => {
    if (commentsContainerRef.current?.scrollTop === 0 && totalResults && totalResults > comments.length) {
      nextPage()
    }
  }

  const onSend = async (content: string) => {
    createComment({ programId, candidateId, content, visibility: 'public' })
  }

  return (
    <SidePanel
      title={t('grants.candidateNotes.notes')}
      onClose={onClose}
      testId="candidate-comments"
      closeButtonTestId="close-comments-button"
    >
      {!comments.length && (
        <Flex justify="center" align="center" flex={1}>
          <IllustratedMessage
            title={t('grants.candidateNotes.noComments.title')}
            description={t('grants.candidateNotes.noComments.description')}
            illustration="no-grant-candidates"
          />
        </Flex>
      )}

      <div ref={commentsContainerRef} className={styles.commentsContainer} onScroll={handleScroll}>
        {isLoadingComments && (
          <Flex className={styles.loaderContainer}>
            <Loader />
          </Flex>
        )}
        {comments
          .sort((a, b) => new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime())
          .map(comment => (
            // TODO: Add isAdmin prop - not sure if we wouldn't have to return it from the API
            <CandidateComment key={comment.id} comment={comment} isAdmin={false} />
          ))}
      </div>

      <Flex className={styles.messageInputContainer}>
        <MessageInput
          placeholder={t('grants.candidateNotes.leaveNote')}
          isLoading={isCreateCommentLoading}
          onSend={onSend}
        />
      </Flex>
    </SidePanel>
  )
}
